import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Tag, Loader2, Calendar, Percent, PoundSterling, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';

/**
 * Promo Codes admin — full CRUD.
 *
 * Codes apply to end-user tier only (server enforces). One promo per order
 * (server enforces). Trade tiers (builder/retailer) never see the input in
 * the cart UI.
 */
const PromoCodesAdmin = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: '', type: 'percent', value: 10, active: true,
    expires_at: '', min_order: 0, max_uses: 0, description: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/promos');
      setPromos(r.data || []);
    } catch (e) {
      toast.error('Could not load promo codes');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error('Code is required');
    if (form.value <= 0) return toast.error('Value must be greater than 0');
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value),
        active: !!form.active,
        min_order: Number(form.min_order) || 0,
        max_uses: Number(form.max_uses) || 0,
        description: form.description || '',
      };
      if (form.expires_at) payload.expires_at = new Date(form.expires_at).toISOString();
      await api.post('/admin/promos', payload);
      toast.success(`Promo ${payload.code} created`);
      setForm({ code: '', type: 'percent', value: 10, active: true, expires_at: '', min_order: 0, max_uses: 0, description: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create promo');
    } finally { setCreating(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/promos/${p.id}`, { active: !p.active });
      setPromos(promos.map(x => x.id === p.id ? { ...x, active: !x.active } : x));
    } catch (e) { toast.error('Update failed'); }
  };

  const deletePromo = async (p) => {
    if (!window.confirm(`Delete promo "${p.code}"?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      setPromos(promos.filter(x => x.id !== p.id));
      toast.success('Deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="promo-codes-admin">
      <div className="bg-white border border-neutral-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-5 h-5 text-[#F46A25]" />
          <h2 className="text-xl font-black text-[#12263A]">Promo Codes</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          Create fixed £ or percent-off codes with optional expiry and usage caps. Codes apply to <b>end-user</b> orders only — trade accounts (builder / retailer) never see the promo input.
        </p>

        <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-5" data-testid="promo-create-form">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Code</label>
            <input
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="WELCOME10"
              required
              data-testid="promo-form-code"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm uppercase font-mono focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Type</label>
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
              data-testid="promo-form-type"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#F46A25]"
            >
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed (£)</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">
              Value {form.type === 'percent' ? '(%)' : '(£)'}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.value}
              onChange={e => setForm({ ...form, value: e.target.value })}
              required
              data-testid="promo-form-value"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Expires</label>
            <input
              type="date"
              value={form.expires_at}
              onChange={e => setForm({ ...form, expires_at: e.target.value })}
              data-testid="promo-form-expires"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Min order (£)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.min_order}
              onChange={e => setForm({ ...form, min_order: e.target.value })}
              data-testid="promo-form-min-order"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div className="md:col-span-5">
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Description (internal)</label>
            <input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Christmas 2026 blast"
              data-testid="promo-form-description"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-neutral-500 block mb-1">Max uses (0 = unlimited)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.max_uses}
              onChange={e => setForm({ ...form, max_uses: e.target.value })}
              data-testid="promo-form-max-uses"
              className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
          <div className="md:col-span-6">
            <button
              type="submit"
              disabled={creating}
              data-testid="promo-create-btn"
              className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create promo
            </button>
          </div>
        </form>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25]" /></div>
        ) : promos.length === 0 ? (
          <p className="text-sm text-neutral-500 italic text-center py-8">No promo codes yet. Create your first one above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promo-codes-table">
              <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-600">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Discount</th>
                  <th className="text-left px-3 py-2">Expires</th>
                  <th className="text-left px-3 py-2">Min order</th>
                  <th className="text-left px-3 py-2">Uses</th>
                  <th className="text-left px-3 py-2">Active</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {promos.map(p => (
                  <tr key={p.id} className="border-t border-neutral-100" data-testid={`promo-row-${p.code}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#12263A]">{p.code}</td>
                    <td className="px-3 py-2">
                      {p.type === 'percent'
                        ? <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3" /> {Number(p.value)}% off</span>
                        : <span className="inline-flex items-center gap-1"><PoundSterling className="w-3 h-3" /> £{Number(p.value).toFixed(2)} off</span>}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {p.expires_at ? (
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.expires_at).toLocaleDateString()}</span>
                      ) : <span className="text-neutral-400">Never</span>}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">{Number(p.min_order || 0) > 0 ? `£${Number(p.min_order).toFixed(2)}` : '—'}</td>
                    <td className="px-3 py-2 text-neutral-600">{p.used_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ''}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggleActive(p)}
                        data-testid={`promo-toggle-${p.code}`}
                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm ${p.active ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-neutral-100 text-neutral-500 border border-neutral-200'}`}
                      >
                        {p.active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        {p.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => deletePromo(p)}
                        data-testid={`promo-delete-${p.code}`}
                        className="text-neutral-400 hover:text-red-600"
                        aria-label={`Delete ${p.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodesAdmin;
